import React, { useState } from "react";

import { Autocomplete, Button, Divider, Stack, styled, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import dayjs from "dayjs";
import { useActionHook, useModals } from "hooks";
// import { NumberInput } from "components/forms";
import {
  useDateFormatSelect,
  useDealActions,
  useDealIdSelector,
  useDealManagementFeeSettingSelect,
  useReportsActions
  // useDealCurrencySelector
} from "store";
import { FeesMethodType } from "store/types";
// import { CurrencyType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { ClassicMethod } from "./ClassicMethod";
import { DifferentialMethod } from "./DifferentialMethod";

// import { currencySign } from "../../../constants";

const methods: { value: FeesMethodType; title: string }[] = [
  { value: "classic", title: "Classic fee structure" },
  { value: "differential", title: "Differential fee structure" }
  // { value: "nav", title: "NAV Fee" }
];

export const managementFeeBasis = ["commitment", "cost", "nav"];

const SectionTitle = styled(Typography)(() => ({
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: 400,
  color: "#071F35",
  borderBottom: "1px solid #DBE8F5"
}));

export interface State {
  method: { value: FeesMethodType; title: string };
  startDate: string | null;
  endDate: string | null;

  differential: { id: number; year: number | null; rate: number | null; type: string }[];
  static: {
    commitment_fees_inv: number | null;
    cost_fees_inv: number | null;
    commitment_fees_post: number | null;
    cost_fees_post: number | null;
  };

  // navFee: number | null;
  // endOfInvestmentPeriod: string | null;
  // fundTerm: number | null;
  // vat: number | null;
  // vatCharge: number | null;
  // minQuarterPay: number | null;
}

const defaultState: State = {
  method: methods[1],
  startDate: "",
  endDate: "",

  differential: [{ id: 1, year: 1, rate: null, type: managementFeeBasis[0] }],
  static: {
    commitment_fees_inv: null,
    cost_fees_inv: null,
    commitment_fees_post: null,
    cost_fees_post: null
  }

  // navFee: null,
  // endOfInvestmentPeriod: null,
  // fundTerm: null,
  // vat: null,
  // vatCharge: null,
  // minQuarterPay: null
};

interface Props {
  onClose: () => void;
}

export const FeesForm: React.FC<Props> = ({ onClose }) => {
  // const dealCurrency = useDealCurrencySelector().find((curr) => curr.is_default)?.value as CurrencyType;

  const {
    method,
    start_date = "",
    end_date = "",
    commitment_fees_inv,
    cost_fees_inv,
    commitment_fees_post,
    cost_fees_post,
    differential_data
  } = useDealManagementFeeSettingSelect() || {};

  const [state, setState] = useState<State>({
    ...defaultState,
    method: methods.find((item) => item.value === method) || methods[1],
    startDate: start_date,
    endDate: end_date,
    static: {
      cost_fees_inv: cost_fees_inv ?? null,
      cost_fees_post: cost_fees_post ?? null,
      commitment_fees_inv: commitment_fees_inv ?? null,
      commitment_fees_post: commitment_fees_post ?? null
    },
    ...(differential_data && {
      differential: differential_data.map((item, index) => ({
        id: index + 1,
        year: item.year,
        rate: item.rate,
        type: item.type
      }))
    })
  });
  const dateFormat = useDateFormatSelect();
  const dealId = useDealIdSelector();

  const { calcManageFeesAction } = useReportsActions();
  const { getDealAndStoreAction } = useDealActions();
  const { errorModal } = useModals();

  const { action, loading } = useActionHook(calcManageFeesAction, {
    onReject: () => {
      errorModal({ msg: "Validation Error" });
    },
    onSuccess: () => {
      if (dealId) {
        getDealAndStoreAction(dealId);
      }
      onClose();
    }
  });

  const handleSubmit = async () => {
    if (!dealId) return;
    action({
      deal_id: dealId,
      method: state.method.value,
      start_date: state.startDate || null,
      end_date: state.endDate || null,

      ...(state.method.value === "differential" && {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        differential_data: state.differential.map(({ id, ...item }) => ({
          year: item.year ?? 0,
          rate: item.rate ?? 0,
          type: item.type ?? ""
        }))
      }),
      ...(state.method.value === "classic" && {
        commitment_fees_inv: state.static.commitment_fees_inv ?? 0,
        cost_fees_inv: state.static.cost_fees_inv ?? 0,
        commitment_fees_post: state.static.commitment_fees_post ?? 0,
        cost_fees_post: state.static.cost_fees_post ?? 0
      })
    });
  };

  return (
    <ProgressOverlay containerSX={{ display: "flex", flexDirection: "column", flexGrow: 1 }} inProgress={loading}>
      <Stack sx={{ flexGrow: 1, p: "20px", gap: "20px" }}>
        <SectionTitle>Management Method</SectionTitle>
        <Autocomplete
          fullWidth
          options={methods}
          disableClearable
          defaultValue={state.method}
          getOptionLabel={(option) => option.title}
          onChange={(_, option) => setState((prevState) => ({ ...prevState, method: option }))}
          renderOption={(props, item) => (
            <li {...props} key={item.value}>
              {item.title}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Management Fee Basis" />}
        />
        <SectionTitle>Management Fees</SectionTitle>
        {state.method.value === "classic" && <ClassicMethod values={state.static} setState={setState} />}
        {state.method.value === "differential" && <DifferentialMethod items={state.differential} setState={setState} />}
        {/* {state.method.value === "nav" && (
          <NumberInput
            value={state.navFee}
            placeholder="%"
            numberInputProps={{
              decimalScale: 2,
              allowNegative: false,
              suffix: "%",
              onValueChange: ({ floatValue }) => setState((prevState) => ({ ...prevState, navFee: floatValue || null }))
            }}
            label="Nav fee"
          />
        )} */}
        <Divider sx={{ borderColor: "#DBE8F5" }} />
        <Stack sx={{ flexDirection: "row", gap: "20px" }}>
          <DesktopDatePicker
            format={dateFormat}
            sx={{ flexGrow: 1, width: "230px" }}
            views={["year", "month", "day"]}
            slotProps={{
              field: {
                clearable: true,
                onClear: () => setState((prevState) => ({ ...prevState, startDate: null }))
              },
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              }
            }}
            value={state.startDate ? dayjs(state.startDate) : null}
            label="Start of Management Fee"
            onChange={(date) => {
              if (date && date.isValid()) {
                setState((prevState) => ({ ...prevState, startDate: toIsoMidday(date?.toISOString()) }));
              }
            }}
          />
          <DesktopDatePicker
            format={dateFormat}
            sx={{ flexGrow: 1, width: "230px" }}
            views={["year", "month", "day"]}
            slotProps={{
              field: {
                clearable: true,
                onClear: () => setState((prevState) => ({ ...prevState, endDate: null }))
              },
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              }
            }}
            value={state.endDate ? dayjs(state.endDate) : null}
            label="End of Investment Period"
            onChange={(date) => {
              if (date && date.isValid()) {
                setState((prevState) => ({ ...prevState, endDate: toIsoMidday(date?.toISOString()) }));
              }
            }}
          />
        </Stack>
        {/* For phase 2 */}
        {/* <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            }
          }}
          value={state.endOfInvestmentPeriod ? dayjs(state.endOfInvestmentPeriod) : null}
          label="End of investment period"
          onChange={(date) => {
            if (date && date.isValid()) {
              setState((prevState) => ({ ...prevState, endOfInvestmentPeriod: toIsoMidday(date?.toISOString()) }));
            }
          }}
        />

        <NumberInput
          value={state.fundTerm}
          numberInputProps={{
            decimalScale: 0,
            allowNegative: false,
            onValueChange: ({ floatValue }) => setState((prevState) => ({ ...prevState, fundTerm: floatValue || null }))
          }}
          label="Fund term (months)"
        />
        <Divider sx={{ borderColor: "#DBE8F5" }} />
        <Stack sx={{ flexDirection: "row", gap: "20px" }}>
          <NumberInput
            fullWidth
            label="VAT"
            placeholder="%"
            value={state.vat}
            numberInputProps={{
              decimalScale: 2,
              suffix: "%",
              onValueChange: ({ floatValue }) => setState((prevState) => ({ ...prevState, vat: floatValue || null }))
            }}
          />
          <NumberInput
            fullWidth
            label="VAT charge"
            placeholder="%"
            value={state.vatCharge}
            numberInputProps={{
              decimalScale: 2,
              suffix: "%",
              onValueChange: ({ floatValue }) =>
                setState((prevState) => ({ ...prevState, vatCharge: floatValue || null }))
            }}
          />
        </Stack>
        <NumberInput
          fullWidth
          label="Minimum quarterly payment"
          placeholder={currencySign[dealCurrency]}
          value={state.minQuarterPay}
          numberInputProps={{
            decimalScale: 2,
            prefix: currencySign[dealCurrency],
            onValueChange: ({ floatValue }) =>
              setState((prevState) => ({ ...prevState, minQuarterPay: floatValue || null }))
          }}
        /> */}
      </Stack>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "20px",
          p: "20px",
          boxShadow: "3px -2px 10px 0px rgba(10, 49, 66, 0.20)"
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Apply
        </Button>
      </Stack>
    </ProgressOverlay>
  );
};
